import { createFileRoute, Link, notFound } from '@tanstack/react-router';
import { SERVICES } from 'features/Landing/content';
import { Photo } from 'features/Landing/Photo';
import { Container, CtaBand, GhostLink, PrimaryLink, TitleBlock } from 'features/Landing/primitives';
import { Faq } from 'features/Landing/sections/Faq';
import { Process } from 'features/Landing/sections/Process';
import { Testimonials } from 'features/Landing/sections/Testimonials';
import { type FC } from 'react';
import { breadcrumbJsonLd, buildHead, faqJsonLd, serviceJsonLd } from 'seo/head';

/** The 0.8fr sidebar column above `lg`, full width below it. */
const ASIDE_PHOTO_SIZES =
  '(min-width: 1152px) 416px, (min-width: 1024px) calc(40vw - 45px), (min-width: 640px) calc(100vw - 64px), calc(100vw - 40px)';

const ServicePage: FC = () => {
  // TanStack's file-route pattern is circular by design: Route is read at render
  // time, long after module init.
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const { service } = Route.useLoaderData();
  const otherServices = SERVICES.filter((s) => s.slug !== service.slug);

  return (
    <>
      <section className="relative overflow-hidden bg-ink-900 pt-32 pb-16 sm:pt-40 sm:pb-24">
        <Photo
          photo={service.image}
          alt=""
          decorative
          loading="eager"
          sizes="100vw"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink-900/95 via-ink-900/70 to-ink-900/40" />
        <div className="relative mx-auto w-full max-w-6xl px-5 sm:px-8">
          <nav aria-label="Breadcrumb" className="font-mono text-xs tracking-wide text-ink-400 uppercase">
            <Link to="/services" className="hover:text-white">
              Services
            </Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span className="text-brand-400">{service.name}</span>
          </nav>
          <h1 className="font-display mt-5 max-w-3xl text-4xl leading-tight font-bold text-white sm:text-5xl">
            {service.heading}
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-ink-300 sm:text-lg">
            {service.summary}
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <PrimaryLink to="/quote">Get a free quote</PrimaryLink>
            <GhostLink to="/projects" dark>
              See our work
            </GhostLink>
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[1.2fr_0.8fr]">
            <div>
              <TitleBlock sheet="DB-01 - The job" title="How we approach it" />
              <p className="text-base leading-relaxed text-ink-700">{service.intro}</p>

              <h3 className="font-display mt-12 text-xl font-bold text-ink-900">
                What&rsquo;s included
              </h3>
              <ul className="mt-5 space-y-3">
                {service.includes.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-ink-700">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      aria-hidden="true"
                      className="mt-0.5 flex-shrink-0 text-brand-600"
                    >
                      <path d="M3.5 9.5 7 13l7.5-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <aside>
              <div className="overflow-hidden rounded-lg border border-ink-200">
                <Photo
                  photo={service.image}
                  alt={service.imageAlt}
                  sizes={ASIDE_PHOTO_SIZES}
                  className="w-full object-cover"
                />
              </div>
              <nav
                aria-label="Other services"
                className="mt-6 rounded-lg border border-ink-200 bg-white p-6"
              >
                <p className="font-mono text-[11px] tracking-[0.2em] text-ink-500 uppercase">
                  Other services
                </p>
                <ul className="mt-4 space-y-2.5">
                  {otherServices.map((other) => (
                    <li key={other.slug}>
                      <Link
                        to="/services/$slug"
                        params={{ slug: other.slug }}
                        className="text-sm font-medium text-ink-700 transition-colors hover:text-brand-600"
                      >
                        {other.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            </aside>
          </div>
        </Container>
      </section>

      <Process />
      <Testimonials />
      <Faq faqs={service.faqs} sheet="DB-04 - Straight answers" title={`${service.name}: common questions`} />
      <CtaBand />
    </>
  );
};

export const Route = createFileRoute('/_landing/services/$slug')({
  loader: ({ params }) => {
    const service = SERVICES.find((s) => s.slug === params.slug);
    // notFound() is TanStack Router's control-flow signal, intentionally not an Error.
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    if (!service) throw notFound();
    return { service };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { service } = loaderData;
    const path = `/services/${service.slug}`;
    return buildHead({
      title: `${service.name} | Dreams Built – Hamilton & Waikato`,
      description: service.summary,
      path,
      jsonLd: [
        serviceJsonLd({ name: service.name, description: service.summary, path }),
        breadcrumbJsonLd([
          { name: 'Home', path: '/' },
          { name: 'Services', path: '/services' },
          { name: service.name, path }
        ]),
        faqJsonLd(service.faqs)
      ]
    });
  },
  component: ServicePage
});
